import { ChangeEvent, useEffect, useState } from "react";
import { AppearanceContextType, useAppearanceContext } from "@contexts/AppearanceContext";
import VolumeIcon from "@icons/VolumeIcon.svg";
import VolumeMutedIcon from "@icons/VolumeMutedIcon.svg";
import "./LifeAtVolumeSlider.scss";

interface LifeAtVolumeSliderProps {
    readonly volume: number;
    readonly setVolume: (newVol: number) => void;
    readonly label?: string;
    readonly overrideIcon?: JSX.Element;
    readonly disabled?: boolean;
    readonly className?: string;
}
/**
 * An abstract volume slider that can be implemented in multiple places in the
 * application.
 */
export default function LifeAtVolumeSlider(props: LifeAtVolumeSliderProps) {
    const { lightMode }: AppearanceContextType = useAppearanceContext();

    const [vol, setVol] = useState<number>(props.volume || 0.0);
    const [lastVol, setLastVol] = useState<number>(0.25); // default value

    function handleVolumeChange(e: ChangeEvent<HTMLInputElement>) {
        const vol: number = parseFloat(e.target.value);
        setVol(vol);
        props.setVolume(vol);
    }

    const toggleVolumeMuted = () => {
        if (!props.disabled) {
            if (vol > 0) {
                // Save the current volume before muting
                setLastVol(vol);
                // Set values to update audio sound and input status.
                setVol(0);
                props.setVolume(0);
            } else {
                if (lastVol !== null) {
                    // If they've toggled before, use the most recent last volume.
                    setVol(lastVol);
                    props.setVolume(lastVol);
                } else {
                    // Otherwise, set the volume to an arbitrary volume that's not too loud.
                    setVol(0.25);
                    props.setVolume(0.25);
                }

                // Then update the last volume so on the next click, the volume is set to 0.
                setLastVol(0);
            }
        }
    };

    // If the volume is updated by parent, update the volume slider
    // (e.g. handle autoplaying space volume on first shuffle in CurrentVirtualSpace)
    useEffect(() => {
        if (props.volume !== vol) {
            setVol(props.volume);
        }
    }, [props.volume]);

    const icon =
        vol > 0 && !props.disabled ? (
            <VolumeIcon className={`volume-icon ${lightMode ? "light" : "dark"}`} />
        ) : (
            <VolumeMutedIcon className={`volume-icon ${lightMode ? "light" : "dark"}`} />
        );

    return (
        <div className={`lifeat-volume-slider ${props.className}`}>
            <span className={`lifeat-volume-slider-label ${lightMode ? "light" : "dark"}`}>{props.label}</span>

            <span className={`volume-slider-toggle`} onClick={toggleVolumeMuted}>
                {props.overrideIcon || icon}
            </span>

            <input
                className={lightMode ? "light" : "dark"}
                type="range"
                min={0}
                max={1}
                step={"any"}
                value={vol}
                disabled={props.disabled}
                onInput={handleVolumeChange}
            />
        </div>
    );
}
